import { useEffect, useState } from "react";
import { api, money, dateIT } from "@/lib/api";
import { PageHeader, Btn, Field, Input, Select, Textarea, Modal, Badge } from "@/components/UI";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Reusable CRUD page.
 * columns: [{ key, label, render?, currency? }]
 * form: [{ key, label, type: text|number|select|textarea|date, options?, required?, colSpan? }]
 */
export default function CrudPage({
  endpoint, eyebrow, title, columns, form, defaultItem = {}, testIdPrefix,
  emptyLabel = "Nessun elemento", extraFilters = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {mode, item}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const save = async () => {
    const payload = { ...modal.item };
    // Coerce numeric fields
    form.forEach((f) => {
      if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "" && payload[f.key] !== null) {
        payload[f.key] = parseFloat(payload[f.key]) || 0;
      }
    });
    // Coerce boolean-select strings ("true"/"false") to real booleans
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "true") payload[k] = true;
      else if (payload[k] === "false") payload[k] = false;
    });
    try {
      if (modal.mode === "create") await api.post(`/${endpoint}`, payload);
      else await api.put(`/${endpoint}/${modal.item.id}`, payload);
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Confermi eliminazione?")) return;
    await api.delete(`/${endpoint}/${id}`);
    load();
  };

  const filtered = items.filter((it) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return Object.values(it).some((v) => typeof v === "string" && v.toLowerCase().includes(s));
  });

  return (
    <div className="fade-in" data-testid={`${testIdPrefix}-page`}>
      <PageHeader
        eyebrow={eyebrow} title={title}
        actions={
          <Btn variant="primary" onClick={() => setModal({ mode: "create", item: { ...defaultItem } })}
            data-testid={`${testIdPrefix}-new-btn`}>
            <Plus size={16} weight="bold" /> Nuovo
          </Btn>
        }
      />

      <div className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm"
              data-testid={`${testIdPrefix}-search`} />
          </div>
          {extraFilters}
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">{emptyLabel}</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th className="w-24">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} data-testid={`${testIdPrefix}-row-${it.id}`}>
                    {columns.map((c) => (
                      <td key={c.key}>
                        {c.render ? c.render(it) :
                          c.currency ? money(it[c.key]) :
                          c.date ? dateIT(it[c.key]) :
                          it[c.key] || "-"}
                      </td>
                    ))}
                    <td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setModal({ mode: "edit", item: { ...it } })}
                          className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-100"
                          data-testid={`${testIdPrefix}-edit-${it.id}`}>
                          <PencilSimple size={15} />
                        </button>
                        <button onClick={() => del(it.id)}
                          className="w-8 h-8 rounded flex items-center justify-center hover:bg-red-50 text-red-600"
                          data-testid={`${testIdPrefix}-del-${it.id}`}>
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === "create" ? `Nuovo ${title}` : `Modifica ${title}`}
        testId={`${testIdPrefix}-modal`}>
        {modal && (
          <div className="grid grid-cols-2 gap-4">
            {form.map((f) => (
              <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : ""}>
                <Field label={f.label + (f.required ? " *" : "")}>
                  {f.type === "select" ? (
                    <Select value={modal.item[f.key] || ""}
                      onChange={(e) => setModal({ ...modal, item: { ...modal.item, [f.key]: e.target.value } })}
                      data-testid={`${testIdPrefix}-field-${f.key}`}>
                      <option value="">-- seleziona --</option>
                      {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea rows={3} value={modal.item[f.key] || ""}
                      onChange={(e) => setModal({ ...modal, item: { ...modal.item, [f.key]: e.target.value } })}
                      data-testid={`${testIdPrefix}-field-${f.key}`} />
                  ) : (
                    <Input type={f.type} value={modal.item[f.key] ?? ""}
                      onChange={(e) => setModal({ ...modal, item: { ...modal.item, [f.key]: e.target.value } })}
                      data-testid={`${testIdPrefix}-field-${f.key}`} />
                  )}
                </Field>
              </div>
            ))}
            <div className="col-span-2 flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Btn variant="secondary" onClick={() => setModal(null)}>Annulla</Btn>
              <Btn variant="primary" onClick={save} data-testid={`${testIdPrefix}-save-btn`}>Salva</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
